import type { PublisherFilterState, PublisherManageDto } from '../../../types/managePublishers';
import { useTranslation } from '../../../i18n/useTranslation';

type DialogKind = 'activate' | 'deactivate' | 'quotas' | 'delete' | 'export';

interface Props {
  selectionCount: number;
  totalCount: number;
  visibleRows: PublisherManageDto[];
  isPageFullySelected: boolean;
  filters: PublisherFilterState;
  onOpenDialog: (kind: DialogKind) => void;
  onSelectAllMatchingFilter: () => Promise<void>;
  onClearSelection: () => void;
}

export default function PublisherBulkActionsBar({
  selectionCount, totalCount, isPageFullySelected, onOpenDialog,
  onSelectAllMatchingFilter, onClearSelection,
}: Props) {
  const { t } = useTranslation();

  if (selectionCount === 0) {
    return (
      <div className="flex items-center justify-between mb-3 text-sm text-neutral-600">
        <span>{t('App.Admin.Publishers.PublisherBulkActionsBar.TotalCount', { count: totalCount })}</span>
        <button
          type="button"
          onClick={() => onOpenDialog('export')}
          className="px-3 py-1 border border-neutral-300 rounded hover:bg-neutral-100"
        >
          {t('App.Admin.Publishers.PublisherBulkActionsBar.ExportToExcel')}
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-3 p-2 bg-primary-50 border border-primary-200 rounded text-sm">
      <div className="flex items-center gap-3">
        <span>{t('App.Admin.Publishers.PublisherBulkActionsBar.SelectedCount', { count: selectionCount })}</span>
        {isPageFullySelected && selectionCount < totalCount && (
          <button
            type="button"
            onClick={onSelectAllMatchingFilter}
            className="text-primary-600 hover:underline"
          >
            {t('App.Admin.Publishers.PublisherBulkActionsBar.SelectAllFilter', { count: Math.min(totalCount, 500) })}
          </button>
        )}
        <button type="button" onClick={onClearSelection} className="text-neutral-600 hover:underline">
          {t('App.Admin.Publishers.PublisherBulkActionsBar.Clear')}
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => onOpenDialog('activate')}   className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherBulkActionsBar.Activate')}</button>
        <button type="button" onClick={() => onOpenDialog('deactivate')} className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherBulkActionsBar.Deactivate')}</button>
        <button type="button" onClick={() => onOpenDialog('quotas')}     className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherBulkActionsBar.EditQuotas')}</button>
        <button type="button" onClick={() => onOpenDialog('delete')}     className="px-3 py-1 bg-white border border-red-300 text-red-700 rounded hover:bg-red-50">{t('App.Admin.Publishers.PublisherBulkActionsBar.Delete')}</button>
        <button type="button" onClick={() => onOpenDialog('export')}     className="px-3 py-1 bg-white border border-neutral-300 rounded hover:bg-neutral-100">{t('App.Admin.Publishers.PublisherBulkActionsBar.Export')}</button>
      </div>
    </div>
  );
}
